import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useMedicareUrl } from 'hooks/useMedicareUrl'
import { ClickableCardLink } from 'components/ClickableCard/ClickableCardLink'
import { ClickableCardInternalLink } from 'components/ClickableCard'
import { MyInternalLink } from 'components/MyLink'
import { IconClipboard } from 'components/Icon/IconClipboard'
import { IconLeaf } from 'components/Icon/IconLeaf'
import { IconLaptop } from 'components/Icon/IconLaptop'
import { CLAIMS_URLS_ENGLISH, CLAIMS_URLS_SPANISH } from '../urls'
import { buildAbsoluteMedicareMyUrl } from 'utils/urls/buildAbsoluteMedicareMyUrl'
import { LaunchDarklyDependentContent } from 'components/LaunchDarklyDependentContent/LaunchDarklyDependentContent'

interface LinkCardProps {
  icon: React.ReactNode
  linkText: string
  linkUrl: string
  elementId: string
  linkIsExternal: boolean
  testID?: string
}

const LinkCard: FC<LinkCardProps> = ({ icon, linkText, linkUrl, elementId, linkIsExternal, testID }) => {
  if (!linkIsExternal) {
    return (
      <ClickableCardInternalLink
      className='my-u-mb--0'
        icon={icon}
        to={linkUrl}
        siteActivityProps={{
          core: {
            coreClickData: { linkText, linkUrl: buildAbsoluteMedicareMyUrl(linkUrl) },
            elementId,
            component: 'Claims'
          }
        }}
        data-testid={testID}
      >
        {linkText}
      </ClickableCardInternalLink>
    )
  } else {
    return (
      <ClickableCardLink
        className='my-u-mb--0'
        href={linkUrl}
        icon={icon}
        siteActivityProps={{
          core: {
            coreClickData: { linkText, linkUrl },
            elementId,
            component: 'Claims'
          }
        }}
        data-testid={testID}
      >
        {linkText}
      </ClickableCardLink>

    )
  }
}

export interface CheckYourClaimsProps {
  isEMsnPreferred?: boolean
}

export const CheckYourClaims: FC<CheckYourClaimsProps> = ({ isEMsnPreferred = false }) => {
  const { t } = useTranslation('claimsSearch')

  const checkMyDeductibleLink = useMedicareUrl(
    CLAIMS_URLS_ENGLISH.claimDeductible,
    CLAIMS_URLS_SPANISH.claimDeductible
  )
  const getEMsnsLink = useMedicareUrl(
    CLAIMS_URLS_ENGLISH.editCommunicationSettings,
    CLAIMS_URLS_SPANISH.editCommunicationSettings
  )

  const emsnPath = '/eMSN'
  const claimDeductiblesPath = '/claims/claim-deductibles'

  return (
    <section className='ds-l-container ds-u-visibility--hidden-print'>
      <div className='ds-l-row'>
        <div className='ds-l-md-col--9'>
          <p>{t('checkYourClaimsDescription')}</p>
          <div className='ds-l-row'>
            <div className='ds-l-md-col--6'>
              <LaunchDarklyDependentContent
                flagName='claimsDeductibleLinking'
                fallbackContent={(
                  <LinkCard
                    icon={<IconClipboard className='my-c-my-svg-icon--primary-alt'/>}
                    linkText={t('checkMyDeductibleAmountLinkText')}
                    linkUrl={checkMyDeductibleLink}
                    elementId='claims_checkdeductible_click'
                    linkIsExternal={true}
                    testID='deductibles-link-legacy'
                  />
                )}>
                <LinkCard
                  icon={<IconClipboard className='my-c-my-svg-icon--primary-alt'/>}
                  linkText={t('checkMyDeductibleAmountLinkText')}
                  linkUrl={claimDeductiblesPath}
                  elementId='claims_checkdeductible_click'
                  linkIsExternal={false}
                  testID='deductibles-link'
                />
              </LaunchDarklyDependentContent>
            </div>
            <div className='ds-u-margin-top--3 ds-u-md-margin-top--0 ds-l-md-col--6'>
            {isEMsnPreferred && (
              <LinkCard
                icon={<IconLaptop className='my-c-my-svg-icon--primary-alt'/>}
                linkText={t('viewEMsnsLinkText')}
                linkUrl={emsnPath}
                elementId='claims_viewemsns_click'
                linkIsExternal={false}
              />
            )}
            {!isEMsnPreferred && (
              <>
                <LinkCard
                  icon={<IconLeaf className='my-c-my-svg-icon--primary-alt'/>}
                  linkText={t('getEMsnsLinkText')}
                  linkUrl={getEMsnsLink}
                  elementId='claims_getemsn_click'
                  linkIsExternal={true}
                />
                <MyInternalLink
                  className='ds-u-margin--1 ds-u-font-size--sm ds-u-display--block ds-u-text-align--right ds-u-color--secondary'
                  to={emsnPath}
                  siteActivityProps={{
                    core: {
                      coreClickData: {
                        linkText: t('viewPreviousEMSNs'), linkUrl: buildAbsoluteMedicareMyUrl(emsnPath)
                      },
                      component: 'Claims',
                      elementId: 'claims_viewpreviousmsns_click'
                    }
                  }}>{t('viewPreviousEMSNs')}</MyInternalLink>
              </>
            )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
